/**
 * API utility with automatic token refresh on 401 errors
 */

const API_BASE_URL = `${process.env.REACT_APP_DOCS_API_KEY}`;

// Mutex to prevent concurrent refresh attempts
let refreshInProgress = false;
let refreshPromise = null;

/**
 * Refresh the access token using the refresh token
 * @returns {Promise<{access_token: string, expires: number, refresh_token: string} | null>}
 */
export const refreshAccessToken = async () => {
  // If a refresh is already in progress, wait for it instead of starting a new one
  if (refreshInProgress && refreshPromise) {
    // Silently wait for existing refresh - no need to log every time
    return refreshPromise;
  }

  // Mark refresh as in progress and create the promise
  refreshInProgress = true;
  refreshPromise = (async () => {
    // Store the original refresh token before making the request
    const originalRefreshToken = sessionStorage.getItem('refresh_token');
    
    try {
      if (!originalRefreshToken) {
        console.warn('[Token Refresh] No refresh token available');
        return null;
      }

      console.log('[Token Refresh] Attempting to refresh access token...');
      
      // Use regular fetch (not apiFetch) to avoid infinite loops
      const response = await fetch(`https://${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json'
        },
        body: JSON.stringify({
          token: originalRefreshToken
        })
      });

      if (!response.ok) {
        console.error(`[Token Refresh] Failed to refresh token: ${response.status}`);
        // Only clear tokens if refresh token is still the same (not already consumed by another request)
        const currentRefreshToken = sessionStorage.getItem('refresh_token');
        if (currentRefreshToken === originalRefreshToken) {
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('refresh_token');
          sessionStorage.removeItem('expires');
          console.log('[Token Refresh] Cleared tokens due to refresh failure');
        } else {
          console.log('[Token Refresh] Refresh token was already updated by another request, skipping token clear');
        }
        return null;
      }

      const data = await response.json();
      console.log('[Token Refresh] Successfully refreshed token');
      
      // Update stored tokens
      if (data.access_token) {
        sessionStorage.setItem('token', data.access_token);
        console.log('[Token Refresh] Updated access token');
      }
      
      if (data.expires) {
        const currentTime = new Date().getTime();
        const expirationTime = currentTime + parseInt(data.expires, 10);
        sessionStorage.setItem('expires', expirationTime.toString());
        console.log(`[Token Refresh] Updated expiration time: ${new Date(expirationTime).toLocaleString()}`);
      }
      
      if (data.refresh_token) {
        sessionStorage.setItem('refresh_token', data.refresh_token);
        console.log('[Token Refresh] Updated refresh token');
      }

      return data;
    } catch (error) {
      console.error('[Token Refresh] Error refreshing token:', error);
      // Only clear tokens if refresh token is still the same
      const currentRefreshToken = sessionStorage.getItem('refresh_token');
      if (currentRefreshToken === originalRefreshToken) {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('refresh_token');
        sessionStorage.removeItem('expires');
        console.log('[Token Refresh] Cleared tokens due to error');
      } else {
        console.log('[Token Refresh] Refresh token was already updated by another request, skipping token clear');
      }
      return null;
    } finally {
      // Reset the mutex
      refreshInProgress = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Check if token is expired
 * @returns {boolean}
 */
const isTokenExpired = () => {
  const expires = sessionStorage.getItem('expires');
  if (!expires) return true;
  
  const expirationTime = parseInt(expires, 10);
  const currentTime = new Date().getTime();
  
  return currentTime >= expirationTime;
};

/**
 * Enhanced fetch with automatic token refresh on 401
 * @param {string} url - The URL to fetch
 * @param {RequestInit} options - Fetch options
 * @param {boolean} retry - Internal flag to prevent infinite retry loops
 * @returns {Promise<Response>}
 */
export const apiFetch = async (url, options = {}, retry = true) => {
  const token = sessionStorage.getItem('token');
  
  // Add authorization header if token exists
  const headers = {
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Make the request
  let response = await fetch(url, {
    ...options,
    headers
  });

  // Handle 401 Unauthorized - try to refresh token (only once)
  if (response.status === 401 && retry) {
    // Check if there's an active stream/job - don't redirect during active processing
    const activeJobId = sessionStorage.getItem('activeJobId');
    const hasActiveStream = activeJobId !== null && activeJobId !== '';
    
    console.log(`[API Fetch] Received 401 for ${url}, attempting token refresh...`);
    const refreshToken = sessionStorage.getItem('refresh_token');
    
    if (refreshToken) {
      // Attempt to refresh the token
      const refreshData = await refreshAccessToken();
      
      // Always get the latest token from sessionStorage after refresh
      // This ensures we use the most up-to-date token even if multiple refreshes happened
      const newToken = sessionStorage.getItem('token');
      
      if (refreshData && newToken) {
        console.log('[API Fetch] Token refreshed, retrying original request...');
        // Retry the original request with the new token from sessionStorage
        // Reading from sessionStorage ensures we use the latest token even if
        // multiple refreshes happened concurrently
        const newHeaders = {
          ...options.headers,
          'Authorization': `Bearer ${newToken}`
        };
        
        response = await fetch(url, {
          ...options,
          headers: newHeaders
        });
        
        // If retry also fails with 401, don't retry again
        if (response.status === 401) {
          console.error('[API Fetch] Retry after refresh also returned 401, refresh token may be invalid');
          // Refresh token might be invalid - clear everything
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('refresh_token');
          sessionStorage.removeItem('expires');
          
          // Don't redirect if there's an active stream - let it handle the error gracefully
          if (!hasActiveStream && window.location.pathname !== '/login') {
            console.log('[API Fetch] Redirecting to login due to invalid refresh token');
            window.location.href = '/login';
          } else if (hasActiveStream) {
            console.log('[API Fetch] Active stream detected - skipping redirect to prevent page reload during processing');
          }
        } else {
          console.log(`[API Fetch] Retry successful: ${response.status}`);
        }
      } else {
        console.error('[API Fetch] Token refresh failed');
        // Refresh failed - clear tokens
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('refresh_token');
        sessionStorage.removeItem('expires');
        
        // Don't redirect if there's an active stream - let it handle the error gracefully
        if (!hasActiveStream && window.location.pathname !== '/login') {
          console.log('[API Fetch] Redirecting to login due to token refresh failure');
          window.location.href = '/login';
        } else if (hasActiveStream) {
          console.log('[API Fetch] Active stream detected - skipping redirect to prevent page reload during processing');
        }
      }
    } else {
      console.warn('[API Fetch] No refresh token available');
      // No refresh token - clear tokens
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('refresh_token');
      sessionStorage.removeItem('expires');
      
      // Don't redirect if there's an active stream - let it handle the error gracefully
      if (!hasActiveStream && window.location.pathname !== '/login') {
        console.log('[API Fetch] Redirecting to login - no refresh token');
        window.location.href = '/login';
      } else if (hasActiveStream) {
        console.log('[API Fetch] Active stream detected - skipping redirect to prevent page reload during processing');
      }
    }
  }

  return response;
};

export default apiFetch;

